import { getCompanyListNotes } from "@/frontend/features/Company/model/getCompanyListNotes";
import { getCompanyMarketOverview } from "@/frontend/features/Company/model/getCompanyMarketOverview";
import { getCompanyPatents } from "@/frontend/features/Company/model/getCompanyPatents";
import { getCompanyProducts } from "@/frontend/features/Company/model/getCompanyProducts";
import { getCompanyProfile } from "@/frontend/features/Company/model/getCompanyProfile";
import { getCompanySimilarCompanies } from "@/frontend/features/Company/model/getCompanySimilarCompanies";
import { getCompanyTeam } from "@/frontend/features/Company/model/getCompanyTeam";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { TParsingStatus } from "@/libs/models/portfolio-companies";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function getCompanyData(companyId: number, isInvestor: boolean) {
  // company details

  const {
    data: detailsData,
    isLoading: isLoadingDetailsData,
    isFetching: isFetchingDetailsData,
    refetch: refetchDetailsData,
    error: detailsDataError
  } = useQuery({
    queryKey: ["companyDetails", companyId],
    queryFn: async () => await getCompanyProfile({ id: companyId }),
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (detailsDataError) {
      console.error(detailsDataError);
      snackbarConstructor("Error fetching company details", "error");
      reportErrorToSentry(detailsDataError, "COMPANY PROFILE: /companies/company/${id}/company-details");
    }
  }, [detailsDataError]);

  // similar companies
  const [similarCompaniesDataStatus, setSimilarCompaniesDataStatus] = useState<TParsingStatus | null>("processing");
  const {
    data: similarCompaniesData,
    isLoading: isLoadingSimilarCompaniesData,
    refetch: refetchSimilarCompaniesData,
    error: similarCompaniesDataError
  } = useQuery({
    queryKey: ["companySimilarCompanies", companyId],
    queryFn: async () => {
      const response = await getCompanySimilarCompanies({ id: companyId });
      setSimilarCompaniesDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: similarCompaniesDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (similarCompaniesDataError) {
      console.error(similarCompaniesDataError);
      setSimilarCompaniesDataStatus("done");
      snackbarConstructor("Error fetching similar companies", "error");
      reportErrorToSentry(similarCompaniesDataError, "COMPANY PROFILE: /companies/company/${id}/similar-companies");
    }
  }, [similarCompaniesDataError]);

  // market overview

  const [marketOverviewDataStatus, setMarketOverviewDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: marketOverviewData,
    isLoading: isLoadingMarketOverviewData,
    refetch: refetchMarketOverviewData,
    error: marketOverviewDataError
  } = useQuery({
    queryKey: ["companyMarketOverview", companyId],
    queryFn: async () => {
      const response = await getCompanyMarketOverview({ id: companyId });
      setMarketOverviewDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: marketOverviewDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (marketOverviewDataError) {
      console.error(marketOverviewDataError);
      setMarketOverviewDataStatus("done");
      snackbarConstructor("Error fetching market overview", "error");
      reportErrorToSentry(marketOverviewDataError, "COMPANY PROFILE: /companies/company/${id}/market-report");
    }
  }, [marketOverviewDataError]);

  // patents

  const {
    data: patentsData,
    isLoading: isLoadingPatentsData,
    refetch: refetchPatentsData,
    error: patentsDataError
  } = useQuery({
    queryKey: ["companyPatents", companyId],
    queryFn: async () => await getCompanyPatents({ id: companyId }),
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (patentsDataError) {
      console.error(patentsDataError);
      snackbarConstructor("Error fetching patents", "error");
      reportErrorToSentry(patentsDataError, "COMPANY PROFILE: /companies/company/${id}/patents");
    }
  }, [patentsDataError]);

  // products
  const [productsDataStatus, setProductsDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: productsData,
    isLoading: isLoadingProductsData,
    refetch: refetchProductsData,
    error: productsDataError
  } = useQuery({
    queryKey: ["companyProducts", companyId],
    queryFn: async () => {
      const response = await getCompanyProducts({ id: companyId });
      setProductsDataStatus(response.status);
      return response;
    },
    refetchInterval: 5000,
    enabled: productsDataStatus !== "done",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (productsDataError) {
      console.error(productsDataError);
      setProductsDataStatus("done");
      snackbarConstructor("Error fetching products", "error");
      reportErrorToSentry(productsDataError, "COMPANY PROFILE: /companies/company/${id}/products");
    }
  }, [productsDataError]);

  // team by Innovera

  const {
    data: teamData,
    isLoading: isLoadingTeamData,
    refetch: refetchTeamData,
    error: teamDataError
  } = useQuery({
    queryKey: ["companyTeam", companyId],
    queryFn: () => getCompanyTeam({ id: companyId }),
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (teamDataError) {
      console.error(teamDataError);
      snackbarConstructor("Error fetching team", "error");
      reportErrorToSentry(teamDataError, "COMPANY PROFILE: /companies/company/${id}/team");
    }
  }, [teamDataError]);

  // notes

  const {
    data: listNotesData,
    isLoading: isLoadingListNotesData,
    refetch: refetchListNotes,
    error: listNotesDataError
  } = useQuery({
    queryKey: ["companyListNotes"],
    queryFn: async () => {
      const response = await getCompanyListNotes({ id: companyId });
      return response;
    },
    gcTime: 0,
    refetchOnWindowFocus: false,
    enabled: isInvestor
  });

  useEffect(() => {
    if (listNotesDataError) {
      console.error(listNotesDataError);
      snackbarConstructor("Error fetching notes", "error");
      reportErrorToSentry(listNotesDataError, "COMPANY PROFILE: /companies/company/${id}/notes");
    }
  }, [listNotesDataError]);

  return {
    detailsData,
    isLoadingDetailsData,
    isFetchingDetailsData,
    refetchDetailsData,
    similarCompaniesData,
    isLoadingSimilarCompaniesData,
    refetchSimilarCompaniesData,
    similarCompaniesDataStatus,
    marketOverviewData,
    isLoadingMarketOverviewData,
    refetchMarketOverviewData,
    marketOverviewDataStatus,
    patentsData,
    isLoadingPatentsData,
    refetchPatentsData,
    productsData,
    isLoadingProductsData,
    refetchProductsData,
    productsDataStatus,
    teamData,
    isLoadingTeamData,
    refetchTeamData,
    listNotesData,
    isLoadingListNotesData,
    refetchListNotes
  };
}
